import { useEffect, useRef } from 'react';

const DEFAULT_INTERVAL = 20000;
const DEFAULT_TIMEOUT = 20000;

/**
 * Вызывает callback с заданным интервалом (по умолчанию DEFAULT_INTERVAL).
 *
 * Защита от параллельных запросов: если isLoading === true, новый вызов
 * пропускается. Если запрос не завершился за timeoutMs (DEFAULT_TIMEOUT) — блокировка
 * снимается принудительно и следующий тик запустит новый вызов.
 */
const useRefresh = (
  callback: () => void,
  depsArray: any[] = [],
  isLoading = false,
  intervalMs = DEFAULT_INTERVAL,
  timeoutMs = DEFAULT_TIMEOUT
) => {
  const savedCallback = useRef(callback);
  const isLoadingRef = useRef(isLoading);
  const inFlightRef = useRef(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    // Если запрос завершился — снимаем флаг inFlight
    if (!isLoading) {
      inFlightRef.current = false;
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    }
  }, [isLoading]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      // Не запускаем, если уже летит запрос
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      isLoadingRef.current = true;

      savedCallback.current();

      // Принудительно снять блокировку через timeoutMs если запрос завис
      timeoutIdRef.current = setTimeout(() => {
        inFlightRef.current = false;
        timeoutIdRef.current = null;
      }, timeoutMs);
    }, intervalMs);

    return () => {
      clearInterval(intervalId);
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...depsArray, intervalMs, timeoutMs]);
};

export default useRefresh;
